import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {AnalyticsService} from '../../logging/analytics.service';
import {NavigationService} from "../../navigation/navigation.service";
import {IdentificationMode} from '../../identity/identification-page/identification-mode';
import {IdentityModel} from '../../identity/identity.model';
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonLayout, ButtonProminence} from "../../buttons/button/button";
import {
    GroupOrderSummaryIconComponent
} from '../../group-order/group-order-summary-icon/group-order-summary-icon.component';
import {GroupOrderModel} from '../../group-order/group-order.model';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {AppliedDealsPanelComponent} from '../../deal/applied-deals-panel/applied-deals-panel.component';
import {DealSummaryIconComponent} from '../../deal/deal-summary-icon/deal-summary-icon.component';
import {DealModel} from '../../deal/deal.model';
import {LoyaltyCardComponent} from '../../loyalty-card/loyalty-card/loyalty-card.component';
import {ButtonShape, ButtonStyle} from '../../configuration/app-entity.model';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

@Component({
    selector: "woa-feature-button-panel",
    templateUrl: "./feature-button-panel.component.html",
    styleUrl: "./feature-button-panel.component.scss",
    imports: [
        AppliedDealsPanelComponent,
        ButtonComponent,
        DealSummaryIconComponent,
        GroupOrderSummaryIconComponent,
        LoyaltyCardComponent,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureButtonPanelComponent {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    private groupOrderModel: GroupOrderModel = inject(GroupOrderModel);
    private dealModel: DealModel = inject(DealModel);
    private navigationService: NavigationService = inject(NavigationService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);

    ButtonProminence = ButtonProminence;
    ButtonStyle = ButtonStyle;
    ButtonLayout = ButtonLayout;
    ButtonShape = ButtonShape;

    public readonly allowShowAppliedDeals: InputSignal<boolean> = input<boolean>(true);

    public onOpenGroupOrder: OutputEmitterRef<void> = output<void>();

    groupOrderHighlighted: WritableSignal<boolean> = signal<boolean>(false);

    public readonly showMemberButton: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.allowMemberLogin() &&
        (this.configurationModel.app()?.promoteMemberLogin || this.configurationModel.app()?.integratedLoyalty) && !this.showLoyaltyCard());

    public readonly showLoyaltyCard: Signal<boolean> = computed<boolean>(() =>
        !this.identityModel.isGuestUser() && this.identityModel.hasActiveLoyaltyCard());

    public readonly showGroupOrderButton: Signal<boolean> = computed<boolean>(() =>
        this.orderingContextModel.currentStore() && this.orderingContextModel.orderType() && this.groupOrderModel.canHaveGroupOrder());

    public readonly dealCodesAvailable: Signal<boolean> = computed<boolean>(() =>
        this.identityModel.currentMember() && this.identityModel.currentMember().anySelectByDealCodeInApp);

    public readonly showDealButton: Signal<boolean> = computed<boolean>(() =>
        (this.dealModel.appliedDeals().length || this.dealCodesAvailable()) && this.configurationModel.isConsumerActor());

    public readonly showActions: Signal<boolean> = computed<boolean>(() =>
        this.showMemberButton() || this.showLoyaltyCard() || this.showGroupOrderButton() || this.showDealButton());

    constructor() {
        effect(() => {
            if (this.groupOrderModel.hasGroupOrder()) {
                this.groupOrderHighlighted.set(true);
                setTimeout(() => {
                    this.groupOrderHighlighted.set(false);
                }, 3200);
            }
        });
    }

    async member(): Promise<void> {
        if (this.identityModel.isGuestUser()) {
            void this.analyticsService.trackFeatureButton("member login");
            return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN});
        } else {
            void this.analyticsService.trackNavOption("profile");
            return this.navigationService.createPage("woa-profile-edit-page");
        }
    }

    async loyaltyCards(): Promise<void> {
        void this.analyticsService.trackNavOption("loyalty-cards");
        return this.navigationService.createPage("woa-loyalty-cards-page");
    }

    async groupOrder(): Promise<void> {
        void this.analyticsService.trackFeatureButton("group order");
        this.onOpenGroupOrder.emit();
    }

    async deal(): Promise<void> {
        void this.analyticsService.trackFeatureButton("deal code");
        return this.navigationService.createPage("woa-deal-code-entry-page");
    }
}
